/**
 * ScheduleView — calendar grid of effective schedules (workers × days).
 *
 * Migrated to live API in Wave 3.2. Calls GET /v1/attendance/schedules/view
 * once per date-range change; the backend synthesizes per-worker,
 * per-day cells from Schedule rows + Timetables so the frontend is
 * essentially a renderer.
 *
 * Backend cell shape per day (ScheduleViewRow.days):
 *   { type: 'fixed' | 'cycle' | 'temporary' | 'unscheduled',
 *     timetable_name: string | null,
 *     color: string | null }
 *
 * UI cell mapping:
 *   - fixed/cycle/temporary with a timetable → 'work' colored by tt.color
 *   - fixed/cycle/temporary without a timetable → 'work' (neutral)
 *   - unscheduled → 'off'
 *
 * Holiday, rest and leave ARE in the response as of 2026-08-12. The backend now resolves each
 * cell through `schedule_resolution.resolve_day_shape`, the same decision
 * `calculation.compute_worker_day` uses for payroll, so this grid and the attendance report can
 * no longer disagree about whether somebody was expected to work. It previously synthesized only
 * schedule rows: a cycle schedule has no `timetable_id` of its own, so every cycle-scheduled day
 * came back as a neutral "work" cell — seven a week — and the legend advertised Leave and Holiday
 * states that no cell could ever take.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography,
  Chip,
} from '@mui/material';
import {
  CloseOutlined, FileDownloadOutlined, PersonOutlined, RefreshOutlined,
  TodayOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type { ScheduleViewDay, ScheduleViewRow } from '../../../api/attendance';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayIso() { return isoDate(new Date()); }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
function daysFromNow(n: number) { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); }

const periodOptions = [
  { value: 'today', i18nKey: 'attendance:scheduleView.today' },
  { value: 'week', i18nKey: 'attendance:scheduleView.thisWeek' },
  { value: 'next_week', i18nKey: 'attendance:scheduleView.nextWeek' },
  { value: 'month', i18nKey: 'attendance:scheduleView.thisMonth' },
];

function periodRange(v: string): [string, string] | null {
  const today = new Date();
  const dow = today.getDay();
  const offsetToMon = dow === 0 ? 6 : dow - 1;
  switch (v) {
    case 'today':
      return [todayIso(), todayIso()];
    case 'week':
      return [daysAgo(offsetToMon), daysFromNow(6 - offsetToMon)];
    case 'next_week':
      return [daysFromNow(7 - offsetToMon), daysFromNow(13 - offsetToMon)];
    case 'month': {
      const start = new Date();
      start.setDate(1);
      const end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      return [isoDate(start), isoDate(end)];
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Date enumeration for column headers
// ---------------------------------------------------------------------------

interface DateCol {
  key: string;       // YYYY-MM-DD (backend key in row.days)
  dayLabel: string;  // 'Mon' | '12'
  subLabel: string;  // 'Apr 21' | 'Apr'
}

function enumerateDates(startIso: string, endIso: string, locale: string): DateCol[] {
  const start = new Date(startIso + 'T00:00:00');
  const end = new Date(endIso + 'T00:00:00');
  if (!Number.isFinite(+start) || !Number.isFinite(+end) || end < start) return [];
  const cols: DateCol[] = [];
  const cursor = new Date(start);
  // Locale-aware day-of-week + month-day formatters. Browser-native
  // Intl works for all 9 locales we support without extra deps.
  const dayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const monDayFmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  // Cap at 45 days so the grid doesn't explode.
  let count = 0;
  while (cursor <= end && count < 45) {
    cols.push({
      key: isoDate(cursor),
      dayLabel: dayFmt.format(cursor),
      subLabel: monDayFmt.format(cursor),
    });
    cursor.setDate(cursor.getDate() + 1);
    count++;
  }
  return cols;
}

// ---------------------------------------------------------------------------
// Cell styling
// ---------------------------------------------------------------------------

/**
 * One grid cell. Aliased from the generated schema rather than hand-declared: it used to be a
 * local interface with three fields while the backend sent an untyped dict, so the two could
 * drift with TypeScript staying green.
 */
type DayInfo = ScheduleViewDay;

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

/**
 * Cell appearance, switched on the resolved `kind`.
 *
 * It used to switch on `type`, which is the SCHEDULE ROW's type — and a cycle schedule carries no
 * `timetable_id`, so every cycle-scheduled day rendered as a neutral "work" cell. A tenant whose
 * cycle gave them weekends off saw seven work days a week, and the backend never sent a holiday,
 * a rest day or a leave day at all.
 */
/**
 * Loose `t` signature, matching `translateApiError`: react-i18next's `TFunction` carries heavy
 * generics that reject an arbitrary string key at compile time, and these helpers live outside
 * the component, so they take it as a parameter.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

function cellColors(day: DayInfo | undefined) {
  const kind = day?.kind;
  if (!day || kind === 'unscheduled') {
    return { bg: 'grey.50', border: 'transparent', text: 'text.disabled' };
  }
  if (kind === 'rest') return { bg: 'grey.100', border: 'transparent', text: 'text.disabled' };
  if (kind === 'holiday') return { bg: 'rgba(237,108,2,0.10)', border: '#ED6C02', text: '#B25000' };
  if (kind === 'leave') return { bg: 'rgba(2,136,209,0.10)', border: '#0288D1', text: '#01579B' };
  const c = day?.color || '#2E65D4';
  return { bg: `${c}15`, border: c, text: c };
}

/** Cell text. Every branch goes through `t()` — these were English literals. */
function cellLabel(day: DayInfo | undefined, t: Translate): string {
  if (!day || day.kind === 'unscheduled') return t('attendance:scheduleView.cell.unscheduled');
  if (day.kind === 'rest') return t('attendance:scheduleView.cell.rest');
  // The holiday's own name is more useful than the word "Holiday", and it is what the calendar
  // page shows for the same date.
  if (day.kind === 'holiday') return day.holiday_name || t('attendance:scheduleView.cell.holiday');
  if (day.kind === 'leave') return t('attendance:scheduleView.cell.leave');
  return day.timetable_name
    || (day.type === 'temporary' ? t('attendance:scheduleView.cell.temporary') : t('attendance:scheduleView.cell.work'));
}

/** Why a cell reads the way it does — shown on hover, so a grey square is explainable. */
function cellTooltip(day: DayInfo | undefined, t: Translate): string {
  if (!day) return '';
  if (day.kind === 'rest') {
    return day.rest_reason === 'cycle'
      ? t('attendance:scheduleView.cell.restBecauseCycle')
      : t('attendance:scheduleView.cell.restBecauseWeekly');
  }
  if (day.kind === 'unscheduled') return t('attendance:scheduleView.cell.unscheduledHint');
  if (day.kind === 'holiday') return day.holiday_name || t('attendance:scheduleView.cell.holiday');
  if (day.kind === 'leave') return t('attendance:scheduleView.cell.leave');
  if (day.timetable_name) return day.timetable_name;
  return t('attendance:scheduleView.cell.work');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DepartmentLite { id: string; name: string }

const ScheduleView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<ScheduleViewRow[]>([]);
  const [depts, setDepts] = useState<DepartmentLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(() => periodRange('week')?.[0] ?? todayIso());
  const [endDate, setEndDate] = useState(() => periodRange('week')?.[1] ?? todayIso());
  const [quickPeriod, setQuickPeriod] = useState('week');
  const [locFilter, setLocFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [workerFilter, setWorkerFilter] = useState('');
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [applied, setApplied] = useState(() => ({
    start: periodRange('week')?.[0] ?? todayIso(),
    end: periodRange('week')?.[1] ?? todayIso(),
    loc: 'all' as string,
    dept: 'all' as string,
  }));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [calendarRow, setCalendarRow] = useState<ScheduleViewRow | null>(null);

  // ---------- data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.schedulesApi.getView({
        start_date: applied.start,
        end_date: applied.end,
        department_id: applied.dept === 'all' ? undefined : applied.dept,
        location_id: applied.loc === 'all' ? undefined : applied.loc,
      });
      setRows(data);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [applied, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    organizationApi.departmentsApi
      .list()
      .then((r) => setDepts(r.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setDepts([]));
    organizationApi.locationsApi
      .list()
      .then((r) => setLocations(r.map((l) => ({ id: l.id, name: l.name }))))
      .catch(() => setLocations([]));
  }, []);

  // ---------- filters ----------
  const handleQuickPeriod = (val: string) => {
    setQuickPeriod(val);
    const r = periodRange(val);
    if (r) {
      setStartDate(r[0]);
      setEndDate(r[1]);
      setApplied((p) => ({ ...p, start: r[0], end: r[1] }));
      setPage(0);
    }
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickPeriod('custom');
  };

  const isDirty =
    applied.start !== startDate ||
    applied.end !== endDate ||
    applied.loc !== locFilter ||
    applied.dept !== deptFilter;

  const handleApply = () => {
    setApplied({ start: startDate, end: endDate, loc: locFilter, dept: deptFilter });
    setPage(0);
  };

  // Client-side worker free-text filter (server doesn't filter by name).
  const filteredRows = useMemo(() => {
    const q = workerFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.worker_name ?? '').toLowerCase().includes(q) ||
        (r.department ?? '').toLowerCase().includes(q),
    );
  }, [rows, workerFilter]);

  const dateCols = useMemo(
    () => enumerateDates(applied.start, applied.end, i18n.language),
    [applied.start, applied.end, i18n.language],
  );
  const isWide = dateCols.length > 7;

  const paginated = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:scheduleView.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:scheduleView.subtitle')}</Typography>
          {/* How this layer interacts with the others.
              Same shape the Rules and Geofences pages already use — a second line of
              small text under the subtitle, not a diagram or a dialog. Added 2026-08-10
              on owner review: those two modules explain their override order and the
              three schedule pages explained nothing, while the schedule chain is the one
              with the surprises. Each sentence answers a specific way an admin gets it
              wrong, and no page repeats the whole chain (the whole chain belongs on the overview, plus the one nobody guesses: no schedule at all means rest, not absence). */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('attendance:scheduleView.precedence')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <DateField value={startDate} onChange={(e) => handleDateChange('start', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>–</Typography>
          <DateField value={endDate} onChange={(e) => handleDateChange('end', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Select value={quickPeriod} onChange={(e) => handleQuickPeriod(e.target.value)} size="small"
            sx={{ minWidth: 110, height: 32, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5, fontWeight: 600 } }}>
            {periodOptions.map((o) => <MenuItem key={o.value} value={o.value}>{t(o.i18nKey)}</MenuItem>)}
            <MenuItem value="custom">{t('common:periodOptions.custom')}</MenuItem>
          </Select>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />}
            onClick={() => setToast(t('attendance:scheduleView.exportToast'))}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}>
            {t('common:actions.export')}
          </Button>
        </Stack>
      </Box>

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3 (Location→Department→Worker) */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}
        onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('attendance:timeEntries.locationLabel')}</InputLabel>
          <Select value={locFilter} label={t('attendance:timeEntries.locationLabel')} onChange={(e) => setLocFilter(e.target.value)}>
            <MenuItem value="all">{t('attendance:timeEntries.allLocations')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('attendance:timeEntries.departmentLabel')}</InputLabel>
          <Select value={deptFilter} label={t('attendance:timeEntries.departmentLabel')} onChange={(e) => setDeptFilter(e.target.value)}>
            <MenuItem value="all">{t('attendance:timeEntries.allDepartments')}</MenuItem>
            {depts.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label={t('attendance:timeEntries.colWorker')} placeholder={t('attendance:scheduleView.workerPlaceholder', { defaultValue: 'Name or department' })}
          value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }} />
        <Button variant="contained" size="small" onClick={handleApply} disabled={!isDirty || loading}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem', minWidth: 64, height: 40 }}>
          {t('common:actions.apply')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredRows.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Active filter chips — §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (applied.loc !== 'all') {
          const name = locations.find((l) => l.id === applied.loc)?.name ?? applied.loc;
          chips.push({ key: 'loc', label: `${t('attendance:timeEntries.locationLabel')}: ${name}`,
            onClear: () => {
              setLocFilter('all');
              setApplied((a) => ({ ...a, loc: 'all' }));
              setPage(0);
            } });
        }
        if (applied.dept !== 'all') {
          const name = depts.find((d) => d.id === applied.dept)?.name ?? applied.dept;
          chips.push({ key: 'dept', label: `${t('attendance:timeEntries.departmentLabel')}: ${name}`,
            onClear: () => {
              setDeptFilter('all');
              setApplied((a) => ({ ...a, dept: 'all' }));
              setPage(0);
            } });
        }
        if (workerFilter.trim()) {
          chips.push({ key: 'worker', label: `${t('attendance:timeEntries.colWorker')}: ${workerFilter.trim()}`,
            onClear: () => { setWorkerFilter(''); setPage(0); } });
        }
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => {
                setLocFilter('all');
                setDeptFilter('all');
                setWorkerFilter('');
                setApplied((a) => ({ ...a, loc: 'all', dept: 'all' }));
                setPage(0);
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('common:actions.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {/* Grid */}
      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, maxHeight: 'calc(100vh - 280px)' }}>
        <Table stickyHeader size="small" sx={{ minWidth: isWide ? 180 + dateCols.length * 52 + 60 : undefined }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 4, bgcolor: 'grey.100', fontWeight: 600, minWidth: 180, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>
                {t('attendance:timeEntries.colWorker')}
              </TableCell>
              {dateCols.map((d) => (
                <TableCell key={d.key} align="center"
                  sx={{ bgcolor: 'grey.100', fontWeight: 600, minWidth: isWide ? 52 : 80, px: 0.5, fontSize: isWide ? '0.6rem' : undefined }}>
                  {d.dayLabel}
                  {!isWide && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                      {d.subLabel}
                    </Typography>
                  )}
                </TableCell>
              ))}
              <TableCell align="center"
                sx={{ position: 'sticky', right: 0, zIndex: 4, bgcolor: 'grey.100', fontWeight: 600, minWidth: 50, boxShadow: '-2px 0 4px rgba(0,0,0,0.06)' }}>
                {t('common:actions.view')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={dateCols.length + 2} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={dateCols.length + 2} align="center" sx={{ py: 6 }}>
                  <PersonOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('attendance:scheduleView.noWorkersFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((entry) => (
                <TableRow key={entry.worker_id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.06)', py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.light', color: 'primary.main' }}>
                        {initials(entry.worker_name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption"
                          sx={{ fontWeight: 600, color: 'text.primary', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.72rem' }}>
                          {entry.worker_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {entry.department || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {dateCols.map((d) => {
                    const day = (entry.days?.[d.key] as DayInfo | undefined) ?? undefined;
                    const colors = cellColors(day);
                    return (
                      <TableCell key={d.key} align="center" sx={{ px: 0.5, py: 0.5 }}>
                        <Tooltip title={cellTooltip(day, t)} placement="top">
                          <Box sx={{ px: 0.5, py: 0.25, borderRadius: 0.75, bgcolor: colors.bg,
                            borderLeft: day && day.kind !== 'unscheduled' ? '2px solid' : 'none', borderColor: colors.border }}>
                            <Typography variant="caption"
                              sx={{ fontWeight: 600, fontSize: isWide ? '0.55rem' : '0.68rem', color: colors.text }}>
                              {cellLabel(day, t)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ position: 'sticky', right: 0, zIndex: 2, bgcolor: 'background.paper', boxShadow: '-2px 0 4px rgba(0,0,0,0.06)', py: 0.5 }}>
                    <IconButton size="small" onClick={() => setCalendarRow(entry)} sx={{ '&:hover': { color: 'primary.main' } }}>
                      <TodayOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        {[
          { label: t('attendance:scheduleView.cell.work'), bg: '#2E65D415', border: '#2E65D4' },
          { label: t('attendance:scheduleView.cell.rest'), bg: 'grey.100', border: 'transparent' },
          { label: t('attendance:scheduleView.cell.holiday'), bg: 'rgba(237,108,2,0.10)', border: '#ED6C02' },
          { label: t('attendance:scheduleView.cell.leave'), bg: 'rgba(2,136,209,0.10)', border: '#0288D1' },
          { label: t('attendance:scheduleView.cell.unscheduled'), bg: 'grey.50', border: 'transparent' },
        ].map((l) => (
          <Stack key={l.label} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: l.bg,
              borderLeft: l.border !== 'transparent' ? `3px solid ${l.border}` : 'none' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{l.label}</Typography>
          </Stack>
        ))}
      </Stack>

      {/* Per-worker calendar dialog */}
      <Dialog open={!!calendarRow} onClose={() => setCalendarRow(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {calendarRow && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.7rem', bgcolor: 'primary.light', color: 'primary.main' }}>
                  {initials(calendarRow.worker_name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{calendarRow.worker_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{calendarRow.department || '—'}</Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setCalendarRow(null)}><CloseOutlined fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75, mt: 1 }}>
                {(() => {
                  // Locale-aware Mon-Sun labels via Intl. Reference dates 2024-01-01 (Mon) ... 2024-01-07 (Sun).
                  const dayFmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
                  return [1, 2, 3, 4, 5, 6, 7].map((dom) => {
                    const ref = new Date(2024, 0, dom);
                    const label = dayFmt.format(ref);
                    return (
                      <Typography key={dom} variant="caption" sx={{ textAlign: 'center', fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>{label}</Typography>
                    );
                  });
                })()}
                {dateCols.map((d) => {
                  const day = (calendarRow.days?.[d.key] as DayInfo | undefined) ?? undefined;
                  const colors = cellColors(day);
                  const dayNum = new Date(d.key + 'T00:00:00').getDate();
                  return (
                    <Box key={d.key}
                      sx={{ p: 0.75, borderRadius: 1, bgcolor: colors.bg,
                        borderLeft: day && day.kind !== 'unscheduled' ? '2px solid' : 'none', borderColor: colors.border,
                        textAlign: 'center', minHeight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.primary' }}>{dayNum}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: colors.text }}>{cellLabel(day, t)}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduleView;
